import { useMemo } from 'react';
import { parseDate, type CalendarDate, type DateValue } from '@internationalized/date';
import { format } from 'date-fns';
import type * as yup from 'yup';
import type {
  OutOfTermItem,
  TermDateBounds,
  TermFreeRange,
  TermScheduledRange,
} from '@/entities/Courses';

/** Available period of the term form as calendar values, inclusive */
export interface TermDatePickerRange {
  min: CalendarDate;
  max: CalendarDate;
}

/**
 * Passed to the term forms as react-hook-form context: the yup tests below read it. The periods
 * come from the backend, which validates the save with the same rule.
 * ranges: null — the backend sent nothing, no limits; [] — no free dates at all
 */
export interface TermDatesContext {
  ranges: TermDatePickerRange[] | null;
  // Why no date can be picked for the typed number: it is taken, or it has no free dates
  blockedMessage: string | null;
  // Numbers the form can't take; the number field is checked against them
  takenNumbers: number[];
}

/** YYYY-MM-DD as a calendar date, without shifting it by the browser time zone */
export const toCalendarDate = (date: string | null | undefined): CalendarDate | null => {
  if (!date) return null;
  try {
    return parseDate(date.slice(0, 10));
  } catch {
    return null;
  }
};

export const toDateOnly = (date: CalendarDate) =>
  `${date.year}-${String(date.month).padStart(2, '0')}-${String(date.day).padStart(2, '0')}`;

const formatCalendarDate = (date: CalendarDate) =>
  format(new Date(date.year, date.month - 1, date.day), 'd MMM yyyy');

const formatDateOnly = (date: string) => {
  const calendarDate = toCalendarDate(date);
  return calendarDate ? formatCalendarDate(calendarDate) : date;
};

const toPickerRange = (bounds: TermDateBounds | null | undefined): TermDatePickerRange | null => {
  const min = toCalendarDate(bounds?.minStartDate);
  const max = toCalendarDate(bounds?.maxEndDate);
  if (!min || !max || min.compare(max) > 0) return null;
  return { min, max };
};

/** Sorted periods with overlapping or adjacent ones joined */
const mergeRanges = (ranges: TermDatePickerRange[]) =>
  [...ranges]
    .sort((a, b) => a.min.compare(b.min))
    .reduce<TermDatePickerRange[]>((merged, range) => {
      const last = merged[merged.length - 1];
      if (last && range.min.compare(last.max) <= 1) {
        if (range.max.compare(last.max) > 0) last.max = range.max;
      } else {
        merged.push({ ...range });
      }
      return merged;
    }, []);

const isWithinRange = (date: DateValue, range: TermDatePickerRange) =>
  date.compare(range.min) >= 0 && date.compare(range.max) <= 0;

/** "Available dates: 1 Apr 2026 – 30 Jun 2026, 1 Oct 2026 – 31 Dec 2026" */
const formatAvailableRanges = (ranges: TermDatePickerRange[]) =>
  `Available dates: ${ranges
    .map((range) => `${formatCalendarDate(range.min)} – ${formatCalendarDate(range.max)}`)
    .join(', ')}`;

/** Free period of the intake for a new term with this number; null — none */
export const findFreeRange = (freeRanges: TermFreeRange[], termNumber: number) =>
  freeRanges.find(
    (range) =>
      (range.afterTermNumber ?? 0) < termNumber &&
      (range.beforeTermNumber === null || termNumber < range.beforeTermNumber),
  ) ?? null;

/** Edited term: its bounds and the numbers of its neighbours, by which its position is known */
export interface EditedTermPosition {
  dateBounds?: TermDateBounds;
  previousNumber: number | null;
  nextNumber: number | null;
}

/** Numbers of the terms right before and after `termNumber`; `numbers` — the other terms */
export const findNeighbourNumbers = (numbers: number[], termNumber: number) => {
  const others = numbers.filter((number) => number !== termNumber);
  const previous = others.filter((number) => number < termNumber);
  const next = others.filter((number) => number > termNumber);
  return {
    previousNumber: previous.length > 0 ? Math.max(...previous) : null,
    nextNumber: next.length > 0 ? Math.min(...next) : null,
  };
};

/**
 * Bounds for the number typed in the form, picked from what the backend computed: the edited term
 * that stays between the same neighbours keeps its own bounds, a number between other terms takes
 * that free period of the intake. undefined — the backend sent nothing, no limits
 */
export const resolveTermDateBounds = (
  termNumber: number,
  freeRanges: TermFreeRange[] | undefined,
  edited?: EditedTermPosition,
): TermDateBounds | null | undefined => {
  if (
    edited &&
    (edited.previousNumber ?? 0) < termNumber &&
    (edited.nextNumber === null || termNumber < edited.nextNumber)
  ) {
    return edited.dateBounds;
  }
  if (!freeRanges) return undefined;
  return findFreeRange(freeRanges, termNumber);
};

export const noFreeDatesMessage = (termNumber: number) =>
  `There are no free dates for Term ${termNumber} in this intake: other terms take them`;

export const termExistsMessage = (termNumber: number) =>
  `Term ${termNumber} already exists. Choose another number`;

const NO_FREE_DATES_IN_INTAKE = 'There are no free dates in this intake: other terms take them';

/**
 * Available periods, hint and blocking message of a term form. Without a number every free period
 * of the intake is available (and the edited term's own one); with a number — its period; a taken
 * number has no dates at all
 */
export const getTermFormDates = (
  termNumber: number | null,
  freeRanges: TermFreeRange[] | undefined,
  edited?: EditedTermPosition,
  takenNumbers: number[] = [],
): { ranges: TermDatePickerRange[] | null; blocked: string | null; hint?: string } => {
  if (!termNumber) {
    if (!freeRanges) return { ranges: null, blocked: null };
    const ranges = mergeRanges(
      [...freeRanges, ...(edited?.dateBounds ? [edited.dateBounds] : [])]
        .map(toPickerRange)
        .filter((range): range is TermDatePickerRange => !!range),
    );
    return {
      ranges,
      blocked: null,
      hint: ranges.length > 0 ? formatAvailableRanges(ranges) : NO_FREE_DATES_IN_INTAKE,
    };
  }
  if (takenNumbers.includes(termNumber)) {
    const message = termExistsMessage(termNumber);
    return { ranges: [], blocked: message, hint: message };
  }
  const resolved = resolveTermDateBounds(termNumber, freeRanges, edited);
  if (resolved === undefined) return { ranges: null, blocked: null };
  const range = toPickerRange(resolved);
  if (!range) {
    const message = noFreeDatesMessage(termNumber);
    return { ranges: [], blocked: message, hint: message };
  }
  return { ranges: [range], blocked: null, hint: formatAvailableRanges([range]) };
};

/**
 * DatePicker limits, as the forms had with `minValue={today}`: days outside the available periods
 * are disabled through minValue/maxValue. Only when several periods are available (no number
 * typed yet) the gaps between them — the dates of other terms — are marked unavailable as well.
 * `within` — the start date: the end must stay in the same period and can't be earlier than it
 */
const getTermDatePickerProps = (
  ranges: TermDatePickerRange[] | null,
  within?: CalendarDate | null,
) => {
  if (!ranges) return within ? { minValue: within } : {};
  const startRange = within ? ranges.find((range) => isWithinRange(within, range)) : undefined;
  const allowed = startRange ? [startRange] : ranges;
  if (allowed.length === 0) return { isDateUnavailable: () => true };
  // Periods are sorted and don't overlap: the first starts earliest, the last ends latest
  const limits = {
    minValue: startRange && within ? within : allowed[0].min,
    maxValue: allowed[allowed.length - 1].max,
  };
  if (allowed.length === 1) return limits;
  return {
    ...limits,
    isDateUnavailable: (date: DateValue) => !allowed.some((range) => isWithinRange(date, range)),
  };
};

/**
 * Props of a term date picker, kept between renders: new callbacks on every render make the open
 * calendar recompute and jump
 */
export const useTermDatePickerProps = (
  ranges: TermDatePickerRange[] | null,
  within?: CalendarDate | null,
) => {
  const withinKey = within?.toString();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => getTermDatePickerProps(ranges, within), [ranges, withinKey]);
};

/** getTermFormDates kept between renders, so the picker props stay the same */
export const useTermFormDates = (
  termNumber: number | null,
  freeRanges: TermFreeRange[] | undefined,
  edited?: EditedTermPosition,
  takenNumbers?: number[],
) => {
  const { dateBounds, previousNumber, nextNumber } = edited ?? {};
  const isEdited = !!edited;
  return useMemo(
    () =>
      getTermFormDates(
        termNumber,
        freeRanges,
        isEdited
          ? { dateBounds, previousNumber: previousNumber ?? null, nextNumber: nextNumber ?? null }
          : undefined,
        takenNumbers,
      ),
    [termNumber, freeRanges, isEdited, dateBounds, previousNumber, nextNumber, takenNumbers],
  );
};

/**
 * Warning shown before saving: the chosen dates don't cover what is already scheduled in the term.
 * Saving is allowed, the admin then edits the sessions or the term dates
 */
export const getScheduledRangeWarning = (
  scheduledRange: TermScheduledRange | null | undefined,
  startDate: CalendarDate | null,
  endDate: CalendarDate | null,
) => {
  if (!scheduledRange || !startDate || !endDate) return null;
  const startsLater = toDateOnly(startDate) > scheduledRange.firstDate;
  const endsEarlier = toDateOnly(endDate) < scheduledRange.lastDate;
  if (!startsLater && !endsEarlier) return null;
  return `Sessions and deadlines of this term are scheduled from ${formatDateOnly(scheduledRange.firstDate)} to ${formatDateOnly(scheduledRange.lastDate)}. With these dates some of them will be outside the term: change their dates or the term dates`;
};

/** Toast after saving a narrowed term */
export const getOutOfTermMessage = (items: OutOfTermItem[]) => {
  const count = items.length;
  const what = count === 1 ? '1 session or deadline is' : `${count} sessions or deadlines are`;
  return `${what} now outside the term dates. Change their dates or the term dates`;
};

const readContext = (context: unknown) => (context ?? {}) as Partial<TermDatesContext>;

/** yup test of the term number (a number or text with one): not taken by another term */
export function testTermNumberFree(this: yup.TestContext, value: unknown) {
  const { takenNumbers = [] } = readContext(this.options.context);
  const digits = typeof value === 'number' ? value : Number(String(value ?? '').match(/\d+/)?.[0]);
  return digits && takenNumbers.includes(digits)
    ? this.createError({ message: termExistsMessage(digits) })
    : true;
}

/** yup test of the start date: within one of the available periods */
export function testTermStartDate(this: yup.TestContext, value: unknown) {
  const { ranges, blockedMessage } = readContext(this.options.context);
  if (!value || !ranges) return true;
  if (blockedMessage) return this.createError({ message: blockedMessage });
  if (ranges.length === 0) return this.createError({ message: NO_FREE_DATES_IN_INTAKE });
  return ranges.some((range) => isWithinRange(value as CalendarDate, range))
    ? true
    : this.createError({ message: formatAvailableRanges(ranges) });
}

/** yup test of the end date: not before the start and in the same available period */
export function testTermEndDate(this: yup.TestContext, value: unknown) {
  const { ranges } = readContext(this.options.context);
  if (!value) return true;
  const date = value as CalendarDate;
  const startDate = (this.parent as { startDate?: CalendarDate | null }).startDate;
  if (startDate && date.compare(startDate) < 0) {
    return this.createError({ message: 'End date must not be earlier than start date' });
  }
  if (!ranges || ranges.length === 0) return true;
  const startRange = startDate ? ranges.find((range) => isWithinRange(startDate, range)) : undefined;
  const allowed = startRange ? [startRange] : ranges;
  return allowed.some((range) => isWithinRange(date, range))
    ? true
    : this.createError({ message: formatAvailableRanges(allowed) });
}
